import D2Asset from '@/editor/assets/d2.asset'
import {
  DEFAULT_TITLE_FONT_ID,
  TITLE_FONT_OPTIONS,
  TITLE_FONT_STYLE_OPTIONS,
  TITLE_FONT_WEIGHT_OPTIONS,
  TITLE_TEXT_ALIGN_OPTIONS,
} from '@/editor/text/fontLibrary'
import { ensureTitleFontLoaded } from '@/editor/text/fontLoader'
import {
  fitTextRenderStateToBox,
  normalizeTextRenderState,
  renderTextThumbnailToCanvas,
  renderTextToCanvas,
} from '@/editor/text/textCanvasUtils'
import {
  TEXT_OUTLINE_WIDTH_FIELD_BOUNDS,
  TEXT_SHADOW_BLUR_FIELD_BOUNDS,
  TEXT_SHADOW_OFFSET_FIELD_BOUNDS,
} from '@/editor/text/textEffectControls'

const DEFAULT_TEXT_FIT_WIDTH_RATIO = 0.62
const DEFAULT_TEXT_FIT_HEIGHT_RATIO = 0.32
const DEFAULT_TEXT_FIT_MIN_SIZE = 12
const DEFAULT_TEXT_THUMB_LONG_EDGE = 480

export default class TextAsset extends D2Asset {

  constructor(uuid) {
    super(uuid)
    this._type = 'text'
    this._props = {
      ...this._props,
      'font.family': DEFAULT_TITLE_FONT_ID,
      'font.size': 96,
      'font.weight': 700,
      'font.style': 'normal',
      'font.color': '#FFFFFF',
      'font.lineHeight': 1.05,
      'font.letterSpacing': 0,
      'font.align': 'center',
      'effect.outlineWidth': 0,
      'effect.outlineColor': '#000000',
      'effect.shadowColor': '#000000',
      'effect.shadowBlur': 0,
      'effect.shadowOffsetX': 0,
      'effect.shadowOffsetY': 0,
    }
  }

  get resizable() { return true }

  get PROPS() {
    return {
      ...super.PROPS,
      font: {
        type: 'object',
        name: 'Font',
        fields: {
          size: { 
            type: 'number',
            name: 'Size',
            tool: 'FontSize',
          },
          family: {
            type: 'string',
            name: 'Family',
            options: TITLE_FONT_OPTIONS,
          },
          weight: {
            type: 'number',
            name: 'Weight',
            options: TITLE_FONT_WEIGHT_OPTIONS,
          },
          style: {
            type: 'string',
            name: 'Style',
            options: TITLE_FONT_STYLE_OPTIONS,
          },
          color: {
            type: 'color',
            name: 'Color (hex)',
            tool: 'ColorSelector'
          },
          lineHeight: {
            type: 'number',
            name: 'Line Height',
            min: 0.7,
            max: 3,
            step: 0.01,
          },
          letterSpacing: {
            type: 'number',
            name: 'Tracking',
            min: -20,
            max: 120,
            step: 1,
          },
          align: {
            type: 'string',
            name: 'Align',
            options: TITLE_TEXT_ALIGN_OPTIONS,
          },
        }
      },
      effect: {
        type: 'object',
        name: 'Effects',
        fields: {
          outlineWidth: {
            type: 'number',
            name: 'Outline',
            ...TEXT_OUTLINE_WIDTH_FIELD_BOUNDS,
          },
          outlineColor: {
            type: 'color',
            name: 'Outline Color',
            tool: 'ColorSelector',
          },
          shadowColor: {
            type: 'color',
            name: 'Shadow Color',
            tool: 'ColorSelector',
          },
          shadowBlur: {
            type: 'number',
            name: 'Shadow Blur',
            ...TEXT_SHADOW_BLUR_FIELD_BOUNDS,
          },
          shadowOffsetX: {
            type: 'number',
            name: 'Shadow X',
            ...TEXT_SHADOW_OFFSET_FIELD_BOUNDS,
          },
          shadowOffsetY: {
            type: 'number',
            name: 'Shadow Y',
            ...TEXT_SHADOW_OFFSET_FIELD_BOUNDS,
          }
        }
      }
    }
  }

  async loadText(text, thumbnail = null, options = {}) {
    this._asset.type = 'text/plain'
    this._asset.meta.text = text
    const nextOptions = thumbnail
      ? options
      : (() => {
        const { state, frame } = this.buildComposedRenderState(
          options?.settings,
          options?.zeroFrame
        )
        return {
          ...options,
          renderState: state,
          thumbnailFrame: frame,
        }
      })()
    await this.prepareText(thumbnail, nextOptions)
    this._asset.ready = true
    this.emit('ready')
  }

  resolveCompositionFrame(settings = null, zeroFrame = null) {
    const frameWidth = Number(zeroFrame?.width)
    const frameHeight = Number(zeroFrame?.height)
    if (Number.isFinite(frameWidth) && frameWidth > 0 && Number.isFinite(frameHeight) && frameHeight > 0) {
      return { width: frameWidth, height: frameHeight }
    }

    const settingsWidth = Number(settings?.width)
    const settingsHeight = Number(settings?.height)
    if (Number.isFinite(settingsWidth) && settingsWidth > 0 && Number.isFinite(settingsHeight) && settingsHeight > 0) {
      return { width: settingsWidth, height: settingsHeight }
    }

    return null
  }

  buildComposedRenderState(settings = null, zeroFrame = null) {
    const baseState = normalizeTextRenderState({
      ...this._props,
      text: this._asset.meta.text,
    })
    const frame = this.resolveCompositionFrame(settings, zeroFrame)
    if (!frame) return { state: baseState, frame }

    return {
      frame,
      state: fitTextRenderStateToBox(baseState, {
        boxWidth: frame.width,
        boxHeight: frame.height,
        widthRatio: DEFAULT_TEXT_FIT_WIDTH_RATIO,
        heightRatio: DEFAULT_TEXT_FIT_HEIGHT_RATIO,
        minFontSize: DEFAULT_TEXT_FIT_MIN_SIZE,
        maxFontSize: baseState['font.size'],
      }),
    }
  }

  applyRenderStateToProps(state) {
    Object.entries(state || {}).forEach(([code, value]) => {
      if (code === 'text') return
      this.pset(code, value)
    })
  }

  async applyDefaultComposition({ settings = null, zeroFrame = null } = {}) {
    const { state, frame } = this.buildComposedRenderState(settings, zeroFrame)
    this.applyRenderStateToProps(state)
    await this.prepareText(null, { renderState: state, thumbnailFrame: frame })
    return state
  }

  prepareText(thumbnail = null, options = {}) {
    return new Promise((resolve) => {
      const run = async () => {
        if (thumbnail) {
          thumbnail.toBlob((blob) => {
            this._asset.thumbnail = blob
            this._asset.meta.width = thumbnail.width
            this._asset.meta.height = thumbnail.height
            resolve()
          }, 'image/png')
          return
        }

        const renderState = options?.renderState || {
          ...this._props,
          text: this._asset.meta.text,
        }

        try {
          await ensureTitleFontLoaded(
            renderState['font.family'],
            renderState['font.weight'],
            renderState['font.style']
          )
        } catch (error) {
          console.warn('prepareText font load failed', error)
        }

        const canvas = document.createElement('canvas')
        const result = renderTextToCanvas(canvas, {
          ...renderState,
        })
        this.applyRenderStateToProps(result.state)

        if (!(result.width > 0) || !(result.height > 0)) {
          this._asset.meta.width = 256
          this._asset.meta.height = 256
          resolve()
          return
        }

        const thumbCanvas = document.createElement('canvas')
        const thumbFrame = options?.thumbnailFrame || null
        const thumbSource = thumbFrame
          ? (() => {
            renderTextThumbnailToCanvas(thumbCanvas, result.state, {
              frameWidth: thumbFrame.width,
              frameHeight: thumbFrame.height,
              longEdge: DEFAULT_TEXT_THUMB_LONG_EDGE,
            })
            return thumbCanvas
          })()
          : canvas

        thumbSource.toBlob((blob) => {
          this._asset.thumbnail = blob
          this._asset.meta.width = result.width
          this._asset.meta.height = result.height
          resolve()
        }, 'image/png')
      }

      run().catch((error) => {
        console.warn('prepareText failed', error)
        this._asset.meta.width = 256
        this._asset.meta.height = 256
        resolve()
      })
    })
  }

  loadFromFile(file) {
    return super.loadFromFile(file)
  }

  updateFrame(time, localTime, frame) {
    const props = super.updateFrame(time, localTime, frame)
    if (!frame.mesh || !frame.controller)
      return props

    if (!frame.material)
      return props
    const appearance = {
      text: this._asset.meta.text,
      'font.family': props['font.family'],
      'font.size': props['font.size'],
      'font.weight': props['font.weight'],
      'font.style': props['font.style'],
      'font.color': props['font.color'],
      'font.lineHeight': props['font.lineHeight'],
      'font.letterSpacing': props['font.letterSpacing'],
      'font.align': props['font.align'],
      'effect.outlineWidth': props['effect.outlineWidth'],
      'effect.outlineColor': props['effect.outlineColor'],
      'effect.shadowColor': props['effect.shadowColor'],
      'effect.shadowBlur': props['effect.shadowBlur'],
      'effect.shadowOffsetX': props['effect.shadowOffsetX'],
      'effect.shadowOffsetY': props['effect.shadowOffsetY'],
    }
    Object.entries(appearance).forEach(([code, value]) => {
      if (code === 'text') return
      this.pset(code, value)
    })
    frame.controller.setState(appearance)
    const liveWidth = Number(frame.controller?.lastLayout?.width)
    const liveHeight = Number(frame.controller?.lastLayout?.height)
    if (Number.isFinite(liveWidth) && liveWidth > 0) this._asset.meta.width = liveWidth
    if (Number.isFinite(liveHeight) && liveHeight > 0) this._asset.meta.height = liveHeight
    frame.material.opacity = props.opacity
    frame.material.transparent = true
    this.dbg.info('updateFrame', frame, props)
    return props
  }

  initAnimation(settings, zeroFrame) {
    const { state } = this.buildComposedRenderState(settings, zeroFrame)
    this.applyRenderStateToProps(state)
    if (typeof document !== 'undefined') {
      const measureCanvas = document.createElement('canvas')
      const layout = renderTextToCanvas(measureCanvas, state)
      this._asset.meta.width = layout.width
      this._asset.meta.height = layout.height
    }

    const animation = super.initAnimation(settings, zeroFrame)
    const defs = this.defaultProps
    // Keep font size stable by default so text is visible immediately.
    const stableSize = Number.isFinite(Number(state['font.size']))
      ? Number(state['font.size'])
      : (Number.isFinite(Number(defs['font.size'])) ? Number(defs['font.size']) : 96)
    if (animation.keyframes[0]) {
      animation.keyframes[0].props['font.size'] = stableSize
      animation.keyframes[0].delay['font.size'] = 0
      animation.keyframes[0].easing['font.size'] = 'Linear.None'
    }
    if (animation.keyframes[1]) {
      animation.keyframes[1].props['font.size'] = stableSize
      animation.keyframes[1].delay['font.size'] = 0
      animation.keyframes[1].easing['font.size'] = 'Linear.None'
    }

    // animation.keyframes[2].props['font.size'] = 0.01
    // animation.keyframes[2].delay['font.size'] = 0
    // animation.keyframes[2].easing['font.size'] = 'Linear.None'

    return animation
  }
  color() {
    return '#dee709'
  }

  icon() {
    return ['fas', 'font']
  }
}
